/**
 * Conflict Display Component (Day-15 Module 8)
 * 
 * Displays sync conflicts with clear, user-friendly messages.
 */

import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  AlertTriangle, Clock, FileText, Pill, CheckSquare, 
  ClipboardList, XCircle, RefreshCw, Info, AlertCircle
} from 'lucide-react';

// ─────────────────────────────────────────────────────────────────
// Conflict Type Definitions (matches backend)
// ─────────────────────────────────────────────────────────────────

export const ConflictTypes = {
  // Shift conflicts
  SHIFT_ALREADY_CLOSED: 'SHIFT_ALREADY_CLOSED',
  SHIFT_NOT_STARTED: 'SHIFT_NOT_STARTED',
  SHIFT_ALREADY_CHECKED_IN: 'SHIFT_ALREADY_CHECKED_IN',
  SHIFT_ALREADY_CHECKED_OUT: 'SHIFT_ALREADY_CHECKED_OUT',
  
  // Timesheet conflicts
  TIMESHEET_ALREADY_SUBMITTED: 'TIMESHEET_ALREADY_SUBMITTED',
  TIMESHEET_ALREADY_APPROVED: 'TIMESHEET_ALREADY_APPROVED',
  TIMESHEET_LOCKED: 'TIMESHEET_LOCKED',
  
  // Medication conflicts
  MED_ALREADY_RECORDED: 'MED_ALREADY_RECORDED',
  MED_SCHEDULE_EXPIRED: 'MED_SCHEDULE_EXPIRED',
  MED_ALREADY_ADMINISTERED: 'MED_ALREADY_ADMINISTERED',
  
  // Task conflicts
  TASK_ALREADY_COMPLETED: 'TASK_ALREADY_COMPLETED',
  TASK_CANCELLED: 'TASK_CANCELLED',
  TASK_REASSIGNED: 'TASK_REASSIGNED',
  
  // Note conflicts
  DUPLICATE_NOTE: 'DUPLICATE_NOTE',
  NOTE_ALREADY_SUBMITTED: 'NOTE_ALREADY_SUBMITTED',
  
  // Incident conflicts
  DUPLICATE_INCIDENT: 'DUPLICATE_INCIDENT',
  INCIDENT_ALREADY_SUBMITTED: 'INCIDENT_ALREADY_SUBMITTED',
  
  // Handover conflicts
  DUPLICATE_HANDOVER: 'DUPLICATE_HANDOVER',
  
  // General conflicts
  DUPLICATE_ACTION: 'DUPLICATE_ACTION',
  RESOURCE_MODIFIED: 'RESOURCE_MODIFIED',
  VERSION_MISMATCH: 'VERSION_MISMATCH',
  ALREADY_SYNCED: 'ALREADY_SYNCED',
};

// ─────────────────────────────────────────────────────────────────
// Human-Readable Messages
// ─────────────────────────────────────────────────────────────────

const CONFLICT_MESSAGES = {
  // Shift conflicts
  [ConflictTypes.SHIFT_ALREADY_CLOSED]: 'This shift has already been closed.',
  [ConflictTypes.SHIFT_NOT_STARTED]: 'You must check in before performing this action.',
  [ConflictTypes.SHIFT_ALREADY_CHECKED_IN]: 'You have already checked in to this shift.',
  [ConflictTypes.SHIFT_ALREADY_CHECKED_OUT]: 'You have already checked out of this shift.',
  
  // Timesheet conflicts
  [ConflictTypes.TIMESHEET_ALREADY_SUBMITTED]: 'This timesheet has already been submitted.',
  [ConflictTypes.TIMESHEET_ALREADY_APPROVED]: 'This timesheet has already been approved.',
  [ConflictTypes.TIMESHEET_LOCKED]: 'This timesheet is locked.',
  
  // Medication conflicts
  [ConflictTypes.MED_ALREADY_RECORDED]: 'Medication already recorded for this time.',
  [ConflictTypes.MED_SCHEDULE_EXPIRED]: 'This medication schedule has expired.',
  [ConflictTypes.MED_ALREADY_ADMINISTERED]: 'This medication has already been administered.',
  
  // Task conflicts
  [ConflictTypes.TASK_ALREADY_COMPLETED]: 'This task has already been completed.',
  [ConflictTypes.TASK_CANCELLED]: 'This task has been cancelled.',
  [ConflictTypes.TASK_REASSIGNED]: 'This task has been reassigned.',
  
  // Note conflicts
  [ConflictTypes.DUPLICATE_NOTE]: 'A progress note with this content already exists.',
  [ConflictTypes.NOTE_ALREADY_SUBMITTED]: 'This note has already been submitted.',
  
  // Incident conflicts
  [ConflictTypes.DUPLICATE_INCIDENT]: 'An incident report already exists.',
  [ConflictTypes.INCIDENT_ALREADY_SUBMITTED]: 'This incident has already been submitted.',
  
  // Handover conflicts
  [ConflictTypes.DUPLICATE_HANDOVER]: 'A handover note already exists.',
  
  // General conflicts
  [ConflictTypes.DUPLICATE_ACTION]: 'This action has already been processed.',
  [ConflictTypes.RESOURCE_MODIFIED]: 'This record was modified by someone else.',
  [ConflictTypes.VERSION_MISMATCH]: 'Data was updated since your last sync.',
  [ConflictTypes.ALREADY_SYNCED]: 'This action has already been synced.',
};

// ─────────────────────────────────────────────────────────────────
// Conflict Icons
// ─────────────────────────────────────────────────────────────────

const CONFLICT_ICONS = {
  [ConflictTypes.SHIFT_ALREADY_CLOSED]: Clock,
  [ConflictTypes.SHIFT_ALREADY_CHECKED_IN]: Clock,
  [ConflictTypes.SHIFT_ALREADY_CHECKED_OUT]: Clock,
  [ConflictTypes.MED_ALREADY_RECORDED]: Pill,
  [ConflictTypes.MED_ALREADY_ADMINISTERED]: Pill,
  [ConflictTypes.TASK_ALREADY_COMPLETED]: CheckSquare,
  [ConflictTypes.DUPLICATE_NOTE]: FileText,
  [ConflictTypes.DUPLICATE_INCIDENT]: AlertCircle,
  [ConflictTypes.DUPLICATE_HANDOVER]: ClipboardList,
  [ConflictTypes.ALREADY_SYNCED]: RefreshCw,
};

// ─────────────────────────────────────────────────────────────────
// Helper Functions
// ─────────────────────────────────────────────────────────────────

export function getConflictMessage(conflictType, serverMessage) {
  return serverMessage || CONFLICT_MESSAGES[conflictType] || `Conflict: ${conflictType}`;
}

export function getConflictIcon(conflictType) {
  return CONFLICT_ICONS[conflictType] || AlertTriangle;
}

export function isRecoverableConflict(conflictType) {
  // These conflicts mean the action was already done - no user action needed
  const autoResolved = [
    ConflictTypes.ALREADY_SYNCED,
    ConflictTypes.SHIFT_ALREADY_CHECKED_IN,
    ConflictTypes.SHIFT_ALREADY_CHECKED_OUT,
    ConflictTypes.MED_ALREADY_RECORDED,
    ConflictTypes.MED_ALREADY_ADMINISTERED,
    ConflictTypes.TASK_ALREADY_COMPLETED,
    ConflictTypes.DUPLICATE_NOTE,
    ConflictTypes.DUPLICATE_INCIDENT,
    ConflictTypes.DUPLICATE_HANDOVER,
    ConflictTypes.DUPLICATE_ACTION,
  ];
  return autoResolved.includes(conflictType);
}

// ─────────────────────────────────────────────────────────────────
// Components
// ─────────────────────────────────────────────────────────────────

/**
 * Single conflict display
 */
export function ConflictAlert({ conflict, onDismiss, onViewServer }) {
  const Icon = getConflictIcon(conflict.conflictType);
  const message = getConflictMessage(conflict.conflictType, conflict.message);
  const isRecoverable = isRecoverableConflict(conflict.conflictType);
  
  return (
    <div 
      className={`flex items-start gap-3 p-4 rounded-lg border ${
        isRecoverable 
          ? 'bg-amber-500/5 border-amber-500/20' 
          : 'bg-red-500/5 border-red-500/20'
      }`}
      data-testid={`conflict-${conflict.conflictType}`}
    >
      <div className={`p-2 rounded-lg ${
        isRecoverable ? 'bg-amber-500/10' : 'bg-red-500/10'
      }`}>
        <Icon className={`h-5 w-5 ${
          isRecoverable ? 'text-amber-600' : 'text-red-600'
        }`} />
      </div>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className={`font-medium text-sm ${
            isRecoverable ? 'text-amber-700' : 'text-red-700'
          }`}>
            {isRecoverable ? 'Already Synced' : 'Conflict'}
          </span>
          <Badge variant="outline" className="text-xs">
            {conflict.actionType || 'Unknown'}
          </Badge>
        </div>
        
        <p className="text-sm text-muted-foreground">{message}</p>
        
        {conflict.serverRecord && (
          <div className="mt-2 p-2 bg-muted/50 rounded text-xs">
            <p className="font-medium text-muted-foreground mb-1">Server Record:</p>
            {conflict.serverRecord.recordedAt && (
              <p>Recorded: {new Date(conflict.serverRecord.recordedAt).toLocaleString()}</p>
            )}
            {conflict.serverRecord.status && (
              <p>Status: {conflict.serverRecord.status}</p>
            )}
            {conflict.serverRecord.id && (
              <p className="text-xs text-muted-foreground/70">ID: {conflict.serverRecord.id}</p>
            )}
          </div>
        )}
        
        <div className="flex gap-2 mt-3">
          {onViewServer && conflict.serverRecord?.id && (
            <Button size="sm" variant="outline" onClick={onViewServer} className="h-7 text-xs">
              <Info className="h-3 w-3 mr-1" />
              View Record
            </Button>
          )}
          {onDismiss && (
            <Button size="sm" variant="ghost" onClick={onDismiss} className="h-7 text-xs">
              <XCircle className="h-3 w-3 mr-1" />
              Dismiss
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Inline conflict message (for forms)
 */
export function ConflictMessage({ conflict, className = '' }) {
  const Icon = getConflictIcon(conflict.conflictType);
  const message = getConflictMessage(conflict.conflictType, conflict.message);
  const isRecoverable = isRecoverableConflict(conflict.conflictType);
  
  return (
    <div 
      className={`flex items-center gap-2 p-3 rounded-lg ${
        isRecoverable 
          ? 'bg-amber-500/10 text-amber-700' 
          : 'bg-red-500/10 text-red-700'
      } ${className}`}
      data-testid="conflict-message"
    >
      <Icon className="h-4 w-4 flex-shrink-0" />
      <span className="text-sm">{message}</span>
    </div>
  );
}

/**
 * Conflicts summary card
 */
export function ConflictsSummary({ conflicts, onResolveAll }) {
  if (!conflicts || conflicts.length === 0) return null;
  
  const recoverable = conflicts.filter(c => isRecoverableConflict(c.conflictType));
  const unrecoverable = conflicts.filter(c => !isRecoverableConflict(c.conflictType));
  
  return (
    <Card className="border-amber-500/20">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          {conflicts.length} Conflict{conflicts.length !== 1 ? 's' : ''} Detected
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {recoverable.length > 0 && (
          <p className="text-xs text-muted-foreground">
            {recoverable.length} already synced (no action needed)
          </p>
        )}
        {unrecoverable.length > 0 && (
          <p className="text-xs text-red-600">
            {unrecoverable.length} need attention
          </p>
        )}
        
        {onResolveAll && recoverable.length === conflicts.length && (
          <Button 
            size="sm" 
            variant="outline" 
            onClick={onResolveAll}
            className="w-full"
          >
            Dismiss All
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Full conflicts list
 */
export function ConflictsList({ conflicts, onDismiss, onDismissAll }) {
  if (!conflicts || conflicts.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <RefreshCw className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p className="text-sm">No conflicts</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-3">
      {onDismissAll && conflicts.length > 1 && (
        <div className="flex justify-end">
          <Button size="sm" variant="outline" onClick={onDismissAll}>
            Dismiss All ({conflicts.length})
          </Button>
        </div>
      )}
      
      {conflicts.map((conflict, index) => (
        <ConflictAlert
          key={conflict.clientActionId || index}
          conflict={conflict}
          onDismiss={onDismiss ? () => onDismiss(conflict) : undefined}
        />
      ))}
    </div>
  );
}

export default ConflictAlert;
